/**
 * Gameplay constants owned by the simulation.
 *
 * These live in sim/ rather than render/ because they are rules, not presentation: the
 * safety ring decides when a vessel is saved, the radar range decides what the player is
 * allowed to know. The render layer re-exports what it needs so tools that must stay
 * engine-free can still read them.
 */

/**
 * The Safe Line: where a friendly stops being the player's problem. Metres.
 *
 * Crossing it resolves the arrival outright — she counts as safe, her cargo lands, she
 * becomes untouchable and anything hunting her breaks off. She does *not* stop or change
 * course: she keeps steaming all the way to the rig, because watching a 180 m hull come
 * the whole way in is what sells the scale of the platform. The line is a rules boundary,
 * not a behaviour one.
 *
 * It replaced the 200 m ring in that role because the ring was ending shifts three to
 * four minutes after they were decided — measured across the campaign, half the game's
 * runtime was the player watching a tanker park with nothing hostile left in the water.
 *
 * 800 m puts the crossing outside the rig's own visual mass and well inside every ambush
 * gate the scripts use, so a wave can never be staged against a hull that is about to
 * become invulnerable. Authoring rule that follows: no ambush gate below 1.2 km.
 */
export const SAFE_LINE_METRES = 800;

/**
 * The docking ring. Presentation only since the Safe Line took over arrival (plan A1):
 * a hull slows here, sits alongside while the transfer plays, then departs. Nothing about
 * the shift's outcome waits on it. Metres.
 */
export const SAFETY_ZONE_RADIUS = 200;

/**
 * How close a hostile hull may come to the platform. Metres, from the rig at the origin.
 *
 * Adam, 2026-07-30: "no enemy vessel aside from the Kraken should be able to go
 * underneath the rig where I can't shoot it." The gun is a fixed mount on the platform
 * edge 105 m up, and while the look limits do let it depress onto water about 35 m out,
 * the structure itself is the real wall: a boat that gets in under the deck and the
 * jacket is hidden behind the rig's own geometry, and no amount of aiming reaches it. A
 * hostile that parks there is unkillable and unfair, whether or not it can shoot back.
 *
 * 100 m keeps every hull outside the footprint with margin, which is comfortably inside
 * the 55 m ring their fire is credited against — so this costs the rig-attack doctrine
 * nothing. It is a hard floor enforced on position, not a steering preference: the AI
 * also aims its goals outside the ring so nobody grinds against the wall, but the clamp
 * is what makes the guarantee true no matter what any future behaviour asks for.
 *
 * The creature is exempt, limbs included. The Kraken's whole finale is that it reaches
 * the platform — `KRAKEN.assaultRangeNear` is 108 m, and the tentacles are supposed to
 * take hold of the legs.
 */
export const RIG_KEEP_OUT_METRES = 100;

/**
 * Where a hit on the platform is *reported*, which is not where the round landed.
 *
 * Incoming fire is aimed at the waterline and has to stay there. The rig has no collision
 * volume, so the only thing the damage model can see is a splash within 55 m of the legs;
 * aiming at the topsides instead was tried and measured, and it fails twice over — rounds
 * pass through an empty deck and splash far outside the ring that credits them, and the
 * elevation change makes a skiff's vacuum-solved aim loft RPGs five hundred metres into
 * the sky on the short shots.
 *
 * So the shell keeps its flat, correct trajectory and the *report* moves instead. Adam's
 * call, 2026-07-30: "it doesn't matter if the shots pass straight through as long as they
 * trigger the sound as they do — the player won't know the difference anyhow, their eyes
 * are on the sea." Everything the player perceives of the impact — the ricochet take, the
 * spark, the direction it comes from — keys off the event position, so putting it beside
 * him at deck height is the whole of what being shot at is supposed to feel like. Just
 * under the 105 m deck, at the platform's own footprint.
 */
export const RIG_HIT_REPORT_POINT = { x: 0, y: 99, z: 4 } as const;

/** Metres of scatter around that point, so sustained fire is not one repeating source. */
export const RIG_HIT_REPORT_SPREAD = 11;

/** Base radar range; the long-range upgrade extends it to 12 km. Metres. */
export const RADAR_RANGE = 10000;
export const RADAR_RANGE_UPGRADED = 12000;

/** Friendlies despawn outbound past this range. Metres. */
export const DESPAWN_RANGE = 12000;

/** Hostiles never spawn beyond this; shift scripts are validated against it. Metres. */
export const MAX_SPAWN_RANGE = 11000;

/** Seconds of continuous detection before a silhouette resolves into an identification. */
export const IDENTIFY_BASE_SECONDS = 2;
